/**
 * Which refusals are OURS — the one product judgement on this page.
 *
 * ── What the wire gives us, and what it deliberately does not ──
 *
 * `refusedBeforeUpstreamCount` is STRUCTURAL: every failure that never dialled
 * an upstream. The gateway's contract is explicit that it "states WHAT
 * happened and nothing about whose fault it is", and it is right to be —
 * baked in there it would be a judgement no consumer could revise.
 *
 * So the scalar cannot be used as "refusals that were not our fault". It
 * contains `MODEL_NOT_AVAILABLE`, which never dialled an upstream precisely
 * BECAUSE WE COULD NOT SERVE THE REQUEST (production: 349 in 24h, 1,614 in
 * 7d). Subtracting those out of the downstream failure rate would report our
 * own inability to route as the gate working correctly — the same shape of
 * error as every other one this page has been rebuilt to avoid, and in the
 * one direction that always flatters us.
 *
 * ── The list, and why each entry is on it ──
 *
 * A refusal is excluded from the downstream rate only when the request was
 * refused ON ITS OWN MERITS — the caller asked for something we do not owe
 * them, and the system behaved correctly by saying no:
 *
 *   REQUEST_NOT_SUPPORTED   the caller sent a request this SKU does not
 *                           serve (production's 7,135: one tenant pointing a
 *                           bare SDK at a CC-exclusive SKU all night)
 *   AUTHENTICATION_FAILED   the caller could not prove who they are
 *   INVALID_REQUEST         the payload was not a request we could parse
 *   INSUFFICIENT_BALANCE    the account has not paid for this
 *   ACCOUNT_UNAVAILABLE     the account is suspended or otherwise not serving
 *
 * Everything else stays counted AGAINST US. `MODEL_NOT_AVAILABLE` is ours: we
 * accepted the tenant, we accepted the request, and then we had nothing to
 * route it to. `'(uncoded)'` — the gateway's placeholder for a refusal that
 * recorded no code — is ours, because "we do not know why we refused this"
 * is not an excuse. And the key space is OPEN: a code introduced after this
 * file was written is ours by default.
 *
 * That default is the whole point of expressing this as a WHITELIST rather
 * than a blacklist. A blacklist would silently absolve every future code the
 * day it shipped, and nobody would notice — the number would just quietly
 * improve. This list must be edited deliberately, and the edit is a product
 * decision about what we are answerable for, not a data-plumbing change.
 *
 * ── What this file must never claim ──
 *
 * Not 防火墙 / admission-rejected, anywhere. `REQUEST_NOT_SUPPORTED` is the
 * admission gate in practice, but the counter is structural and the console
 * has no evidence of the mechanism. Name the code, describe what it means,
 * and stop there.
 */

import type { I18nKey } from '../../i18n/index.ts';
import {
  EXPECTED_REFUSAL_CODES,
  expectedRefusalCount,
  isExpectedRefusal,
} from '../../components/trafficHealth.ts';

// The classification itself lives in `components/trafficHealth.ts`, beside
// the thresholds, because the traffic monitor needs it too — and while it
// lived here, where that page could not import it, the two surfaces
// reported the same window as 0.5% and 2.0%. Re-exported so this module
// stays the page's single entry point for everything refusal-shaped.
export { EXPECTED_REFUSAL_CODES, expectedRefusalCount, isExpectedRefusal };

export interface RefusalGroup {
  readonly code: string;
  readonly count: number;
  /** True when this code leaves the downstream rate. */
  readonly expected: boolean;
}

/**
 * The refusal map as a sorted breakdown, biggest first.
 *
 * `null` in, `null` out: the map is carried only on the window `summary` and
 * on `products.skus` rows, and its absence means "not reported at this
 * grain", never "no refusals". An EMPTY map is a reported zero and comes back
 * as an empty array, which is a different and legitimate answer.
 */
export function refusalGroups(
  byCode: Readonly<Record<string, number>> | null | undefined,
): readonly RefusalGroup[] | null {
  if (byCode === null || byCode === undefined) return null;
  return Object.entries(byCode)
    .filter(([, count]) => count > 0)
    .map(([code, count]) => ({ code, count, expected: isExpectedRefusal(code) }))
    .sort((a, b) => (b.count !== a.count ? b.count - a.count : a.code.localeCompare(b.code)));
}

/**
 * Plain-language names. The raw code is the fallback for anything unmapped —
 * never a blank, and never a guess at what a new code means.
 */
const CODE_LABEL_KEY: Readonly<Record<string, I18nKey>> = {
  REQUEST_NOT_SUPPORTED: 'health_perspective.refusal.request_not_supported',
  AUTHENTICATION_FAILED: 'health_perspective.refusal.authentication_failed',
  INVALID_REQUEST: 'health_perspective.refusal.invalid_request',
  INSUFFICIENT_BALANCE: 'health_perspective.refusal.insufficient_balance',
  ACCOUNT_UNAVAILABLE: 'health_perspective.refusal.account_unavailable',
  MODEL_NOT_AVAILABLE: 'health_perspective.refusal.model_not_available',
  '(uncoded)': 'health_perspective.refusal.uncoded',
};

export function refusalCodeLabel(code: string, t: (key: I18nKey) => string): string {
  const key = CODE_LABEL_KEY[code];
  return key === undefined ? code : t(key);
}
